import type { Attachment } from '../../attachments';
import type { Comment } from '../../comments';
import type { Customer } from '../../customers';
import type { Item } from '../../items';
import type { Location } from '../../locations';
import type { BufferTimeInfo, ItemQuantityWithKit, ItemThumbnailSummary } from '../../types';

// Enums and types
export enum ReservationStatus {
	CREATING = 'creating',
	OPEN = 'open',
	CLOSED = 'closed',
	CLOSED_MANUALLY = 'closed_manually',
	CANCELLED = 'cancelled',
}

export type ReservationStatusType = `${ReservationStatus}`;

export type RepeatFrequency =
	| 'every_2_months'
	| 'every_2_weeks'
	| 'every_3_months'
	| 'every_6_months'
	| 'every_day'
	| 'every_month'
	| 'every_week'
	| 'every_weekday'
	| 'every_year';

export type CustomFieldKey = `fields.${string}` | `customer.fields.${string}` | `customer.${string}`;
export type SystemField =
	| 'from'
	| 'to'
	| 'contact'
	| 'name'
	| 'items'
	| 'location'
	| 'cancelled'
	| 'closed_manually'
	| 'reserved'
	| 'created'
	| 'duration'
	| 'number'
	| 'label';
export type Field = SystemField | CustomFieldKey;

export const RESERVATION_SYSTEM_FIELDS = [
	{
		id: 'from',
		name: 'From',
		kind: 'datetime',
		editor: 'date',
		unit: null,
		excludeFromFilters: true,
	},
	{
		id: 'to',
		name: 'To',
		kind: 'datetime',
		editor: 'date',
		unit: null,
		excludeFromFilters: true,
	},
	{
		id: 'contact',
		name: 'User',
		kind: 'customer',
		unit: null,
		excludeFromFilters: true, // covered by the users filter
	},
	{
		id: 'name',
		name: 'Name',
		kind: 'string',
		editor: 'text',
		unit: null,
	},
	{
		id: 'items',
		name: 'Items',
		kind: 'string',
		excludeFromFilters: true,
	},
	{
		id: 'location',
		name: 'Location',
		kind: 'string',
		editor: 'text',
		unit: null,
		excludeFromFilters: true,
	},
	{
		id: 'cancelled',
		name: 'Cancelled',
		kind: 'datetime',
		editor: 'date',
		unit: null,
	},
	{
		id: 'closed_manually',
		name: 'Closed manually',
		kind: 'datetime',
		editor: 'date',
		unit: null,
		excludeFromFilters: true,
	},
	{
		id: 'closed',
		name: 'Converted',
		kind: 'datetime',
		editor: 'date',
		unit: null,
	},
	{
		id: 'reserved',
		name: 'Reserved',
		kind: 'datetime',
		editor: 'date',
		unit: null,
		excludeFromFilters: true,
	},
	{
		id: 'created',
		name: 'Created',
		kind: 'datetime',
		editor: 'date',
		unit: null,
	},
	{
		id: 'duration',
		name: 'Duration',
		kind: 'string',
		editor: 'text',
		unit: null,
		excludeFromFilters: true,
	},
	{
		id: 'number',
		name: 'Number',
		kind: 'string',
		editor: 'text',
		unit: null,
		excludeFromFilters: true,
	},
	{
		id: 'label',
		name: 'Label',
		kind: 'string',
		editor: 'text',
		unit: null,
		excludeFromFilters: true,
	},
];

export const USER_SYSTEM_FIELDS = [
	{
		id: 'customer.email',
		name: 'User email',
		kind: 'string',
		editor: 'email',
		unit: null,
	},
];

export const fieldIdsByName = {
	from: 'fromDate' as const,
	to: 'toDate' as const,
	contact: 'customer' as const,
};

/** Default fields for single-reservation fetch. */
export const RESERVATION_FIELDS = [
	'_id',
	'name',
	'number',
	'status',
	'label',
	'itemSummary',
	'customer._id',
	'customer.status',
	'customer.name',
	'customer.user',
	'customer.kind',
	'location._id',
	'location.name',
	'itemQuantitiesWithKits',
	'fromDate',
	'toDate',
	'created',
	'reserved',
	'cancelled',
	'closed',
	'closed_manually',
	'archived',
	'repeatId',
	'repeatFrequency',
	'order._id',
	'order.customer',
] as const;

// Types
export interface Reservation {
	_id: string;
	attachments?: Attachment[];
	comments?: Comment[];
	barcodes: string[];
	rfidcodes: string[];
	label: string | null;
	by: string;
	created: string;
	modified: string;
	fields: Record<string, string>;
	/**
	 * @deprecated use itemQuantitiesWithKits
	 * @see itemQuantitiesWithKits
	 */
	items: Item[];
	status: ReservationStatus;
	reserved: string | null;
	cancelled: string | null;
	closed: string | null;
	closed_manually: string | null;
	fromDate: string | null;
	toDate: string | null;
	customer: Customer | null;
	location: Location | null;
	itemSummary: string;
	name: string | null;
	number: string | null;
	archived: string | null;
	repeatId: string | null;
	repeatFrequency: RepeatFrequency | null;
	/**
	 * @deprecated use itemQuantitiesWithKits instead
	 * @see itemQuantitiesWithKits
	 */
	itemQuantities: Record<string, number>;
	itemQuantitiesWithKits: ItemQuantityWithKit[];
	order: {
		_id: string;
		customer: string;
	} | null;
	itemThumbnails: ItemThumbnailSummary;
	bufferTime?: BufferTimeInfo | null;
}

// Variables
export interface ReservationIdVariables {
	reservationId: string;
}

export interface ReservationIdsVariables {
	reservationIds: string[];
}

export interface ReservationIdWithReasonVariables extends ReservationIdVariables {
	reason: string;
}

export interface ReservationIdsWithReasonVariables extends ReservationIdsVariables {
	reason: string;
}
